import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { fetchJson, reportError } from '../../utils/errors';
import { RoomType } from '../../types';

const endpoint = process.env.REACT_APP_TOKEN_ENDPOINT || '/token';
const endpointVideo = process.env.REACT_APP_TOKEN_ENDPOINT || '/token-video';

export function getPasscode() {
  const match = window.location.search.match(/passcode=(.*)&?/);
  const passcode = match ? match[1] : window.sessionStorage.getItem('passcode');
  return passcode;
}

export function fetchToken(
  name: string,
  room: string,
  password: string,
  passcode: string,
  create_room = true,
  create_conversation = process.env.REACT_APP_DISABLE_TWILIO_CONVERSATIONS !== 'true'
) {
  return fetchJson<{ room_type: RoomType; token: string }>(endpointVideo, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({
      user_identity: name,
      room_name: room,
      room_password: password,
      passcode,
      create_room,
      create_conversation,
    }),
  });
}

export function fetchTokenPasscode(
  name: string,
  passcode: string,
  create_room = true,
  create_conversation = process.env.REACT_APP_DISABLE_TWILIO_CONVERSATIONS !== 'true'
) {
  return fetchJson<{ room_type?: RoomType; token?: string }>(endpoint, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({
      user_identity: name,
      passcode,
      create_room,
      create_conversation,
    }),
  });
}

export function verifyPasscode(passcode: string) {
  return fetchTokenPasscode('temp-name', passcode, false /* create_room */, false /* create_conversation */)
    .then(jsonResponse => {
      if (jsonResponse.token) {
        return { isValid: true };
      }

      return { isValid: false, error: 'Passcode is incorrect' };
    })
    .catch(error => ({ isValid: false, error: getErrorMessage(error.userMessage || error.message) }));
}

export function getErrorMessage(message: string) {
  switch (message) {
    case 'passcode incorrect':
      return 'Passcode is incorrect';
    case 'passcode expired':
      return 'Passcode has expired';
    default:
      return message;
  }
}

export default function usePasscodeAuth() {
  const history = useHistory();

  const [user, setUser] = useState<{ displayName: undefined; photoURL: undefined; passcode: string } | null>(null);
  const [isAuthReady, setIsAuthReady] = useState(false);

  const getToken = useCallback(
    (name: string, room: string, password: string) => {
      return fetchToken(name, room, password, user!.passcode).catch(error => {
        throw reportError(error, {
          source: 'auth',
          technicalMessage: 'Failed to fetch a room token with passcode authentication',
          userMessage: getErrorMessage(error.userMessage || error.message),
          retryable: true,
        });
      });
    },
    [user]
  );

  const updateRecordingRules = useCallback(
    async (room_sid, rules) => {
      return fetchJson<object>('/recordingrules', {
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ room_sid, rules, passcode: user?.passcode }),
        method: 'POST',
      }).catch(error => {
        throw reportError(error, {
          source: 'recording',
          technicalMessage: 'Failed to update recording rules for passcode auth',
          userMessage: 'There was an error updating recording rules',
          retryable: true,
        });
      });
    },
    [user]
  );

  useEffect(() => {
    const passcode = getPasscode();

    if (passcode) {
      verifyPasscode(passcode)
        .then(verification => {
          if (verification?.isValid) {
            setUser({ passcode } as any);
            window.sessionStorage.setItem('passcode', passcode);
            history.replace(window.location.pathname);
          }
        })
        .then(() => setIsAuthReady(true));
    } else {
      setIsAuthReady(true);
    }
  }, [history]);

  const signIn = useCallback((passcode: string) => {
    return verifyPasscode(passcode).then(verification => {
      if (verification?.isValid) {
        setUser({ passcode } as any);
        window.sessionStorage.setItem('passcode', passcode);
      } else {
        throw new Error(getErrorMessage(verification?.error || 'Authentication failed'));
      }
    });
  }, []);

  const signOut = useCallback(() => {
    setUser(null);
    window.sessionStorage.removeItem('passcode');
    return Promise.resolve();
  }, []);

  return { user, isAuthReady, getToken, signIn, signOut, updateRecordingRules };
}
